import { useState } from "react";
import { TabbedModal, type Tab } from "../TabbedModal";
import styles from "./DetailModal.module.css";

function CopyJsonButton({ json }: { json: string }) {
  const [copied, setCopied] = useState(false);

  async function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      type="button"
      className={styles.copyJsonButton}
      onClick={handleClick}
      title={copied ? "Copied!" : "Copy JSON to clipboard"}
      aria-label="Copy JSON to clipboard"
    >
      {copied ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
      {copied ? "Copied" : "Copy JSON"}
    </button>
  );
}

export type DetailField<T> = {
  key: keyof T;
  label: string;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
};

export type DetailFieldGroup<T> = {
  heading: string;
  fields: DetailField<T>[];
};

type DetailModalProps<T> = {
  isOpen: boolean;
  onClose: () => void;
  item: T | null;
  title?: string;
  fields?: DetailField<T>[];
  fieldGroups?: DetailFieldGroup<T>[];
  renderValue: (item: T, key: keyof T) => React.ReactNode;
};

export function DetailModal<T>({
  isOpen,
  onClose,
  item,
  title = "Details",
  fields,
  fieldGroups,
  renderValue,
}: DetailModalProps<T>) {
  if (!item) return null;

  const json = JSON.stringify(item, null, 2);

  const tabs: Tab[] = [
    {
      id: "formatted",
      label: "Formatted",
      content: fieldGroups ? (
        <div className={styles.overviewContent}>
          {fieldGroups.map((group, index) => (
            <section key={index} className={styles.overviewSection}>
              <h3 className={styles.sectionHeading}>{group.heading}</h3>
              <table className={styles.detailTable}>
                <tbody>
                  {group.fields.map(field => (
                    <tr key={String(field.key)}>
                      <th className={styles.detailLabel}>{field.label}</th>
                      <td className={styles.detailValue}>{renderValue(item, field.key)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      ) : (
        <div className={styles.formattedModalContent}>
          <table className={styles.detailTable}>
            <tbody>
              {fields?.map(field => (
                <tr key={String(field.key)}>
                  <th className={styles.detailLabel}>{field.label}</th>
                  <td className={styles.detailValue}>{renderValue(item, field.key)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
    },
    {
      id: "json",
      label: "JSON",
      content: <div className={styles.jsonModalContent}>{json}</div>,
    },
  ];

  return (
    <TabbedModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      tabs={tabs}
      actions={<CopyJsonButton json={json} />}
    />
  );
}
